import {
  confirmAppointment,
  cancelAppointment,
} from "../../../../../../api/appointment";
import styles from "./AppointmentList.module.scss";
import AppointmentDetailModal from "../AppointmentDetailModal/AppointmentDetailModal";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";

const STATUS_LABELS = {
  pending: "En attente",
  confirmed: "Confirmé",
  cancelled: "Annulé",
};

const STATUS_COLORS = {
  pending: "#f0ad4e",
  confirmed: "#5bc0de",
  cancelled: "#c0392b",
};

function AppointmentList({ appointments }) {
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selectedAppointment, setSelectedAppointment] = useState(null);

  useEffect(() => {
    const groupAppointments = () => {
      const grouped = {};

      appointments.forEach((appointment) => {
        const { appointment_id, prestation, type_de_prestation, ...rest } =
          appointment;

        if (!grouped[appointment_id]) {
          grouped[appointment_id] = {
            appointment_id,
            ...rest,
            status: rest.status || "pending",
            prestations: [
              {
                type: type_de_prestation,
                prestation: prestation,
              },
            ],
          };
        } else {
          grouped[appointment_id].prestations.push({
            type: type_de_prestation,
            prestation: prestation,
          });
        }
      });

      return grouped;
    };

    const applyFilter = (grouped) => {
      return Object.values(grouped).filter((appointment) => {
        switch (filter) {
          case "confirmed":
            return appointment.status === "confirmed";
          case "notConfirmed":
            return appointment.status === "pending";
          case "cancelled":
            return appointment.status === "cancelled";
          case "all":
          default:
            return true;
        }
      });
    };

    const sortByMostRecent = (list) => {
      return [...list].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
    };

    setFilteredAppointments(sortByMostRecent(applyFilter(groupAppointments())));
  }, [appointments, filter]);

  const updateLocalStatus = (appointmentId, changes) => {
    setFilteredAppointments((prev) => {
      const updated = prev.map((appointment) =>
        appointment.appointment_id === appointmentId
          ? { ...appointment, ...changes }
          : appointment,
      );

      const filtered = updated.filter((appointment) => {
        switch (filter) {
          case "confirmed":
            return appointment.status === "confirmed";
          case "notConfirmed":
            return appointment.status === "pending";
          case "cancelled":
            return appointment.status === "cancelled";
          default:
            return true;
        }
      });

      return [...filtered].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at),
      );
    });
  };

  const handleConfirm = async (appointmentId) => {
    const appointmentToConfirm = filteredAppointments.find(
      (appointment) => appointment.appointment_id === appointmentId,
    );

    if (!appointmentToConfirm) {
      console.error("Rendez-vous non trouvé");
      return;
    }

    try {
      const response = await confirmAppointment(
        appointmentId,
        appointmentToConfirm,
      );
      if (response.status === 200) {
        updateLocalStatus(appointmentId, {
          status: "confirmed",
          is_confirmed: true,
        });
        toast.success(
          "Rendez-vous confirmé : un mail de confirmation a été envoyé à la cliente",
        );
        setSelectedAppointment(null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de la confirmation du rendez-vous");
    }
  };

  const handleCancel = async (appointmentId, reason) => {
    try {
      const response = await cancelAppointment(appointmentId, reason);
      if (response.status === 200) {
        updateLocalStatus(appointmentId, {
          status: "cancelled",
          cancel_reason: reason,
        });
        toast.success(
          "Rendez-vous annulé : un mail d'annulation a été envoyé à la cliente",
        );
        setSelectedAppointment(null);
      }
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l'annulation du rendez-vous");
    }
  };

  return (
    <div className={styles.container}>
      <h2>Liste des rendez-vous</h2>
      <select value={filter} onChange={(e) => setFilter(e.target.value)}>
        <option value="all">Tous les rendez-vous</option>
        <option value="confirmed">Rendez-vous confirmés</option>
        <option value="notConfirmed">Rendez-vous non confirmés</option>
        <option value="cancelled">Rendez-vous annulés</option>
      </select>
      {filteredAppointments.length > 0 ? (
        <table className={styles.tableContainer}>
          <thead>
            <tr>
              <th>Nom</th>
              <th>Prénom</th>
              <th>Prestation(s)</th>
              <th>Adresse</th>
              <th>Date</th>
              <th>Créneau</th>
              <th>Statut</th>
            </tr>
          </thead>
          <tbody>
            {filteredAppointments.map((appointment) => (
              <tr
                key={appointment.appointment_id}
                className={styles.appointmentRow}
                onClick={() => setSelectedAppointment(appointment)}
              >
                <td>{appointment.lastname}</td>
                <td>{appointment.firstname}</td>
                <td>
                  <ul>
                    {appointment.prestations.map((item, index) => (
                      <li key={index}>
                        {item.type} : {item.prestation}
                      </li>
                    ))}
                  </ul>
                </td>
                <td>
                  <ul>
                    <li>{appointment.address?.split(",")[0]}</li>
                    <li>{appointment.address?.split(",")[1]}</li>
                  </ul>
                </td>
                <td>
                  {appointment.date
                    ? new Date(appointment.date).toLocaleDateString("fr-FR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                      })
                    : "—"}
                </td>
                <td>
                  {appointment.start_time?.slice(0, 5) ?? "—"} -{" "}
                  {appointment.end_time?.slice(0, 5) ?? "—"}
                </td>
                <td>
                  <span
                    className={styles.statusBadge}
                    style={{
                      backgroundColor:
                        STATUS_COLORS[appointment.status] || "#999",
                    }}
                  >
                    {STATUS_LABELS[appointment.status] || "En attente"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className={styles.emptyState}>
          Aucun rendez-vous trouvé pour ce filtre.
        </div>
      )}

      <AppointmentDetailModal
        appointment={selectedAppointment}
        isOpen={!!selectedAppointment}
        onClose={() => setSelectedAppointment(null)}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </div>
  );
}

export default AppointmentList;
